// src/pages/DuyurularPage.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Create,
  CreateButton,
  Datagrid,
  EditBase,
  EditButton,
  ExportButton,
  List,
  SaveButton,
  SimpleForm,
  TextInput,
  DateInput,
  BooleanInput,
  SelectInput,
  Toolbar,
  TopToolbar,
  useEditContext,
  useListContext,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useUpdate,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  GlobalStyles,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ImageIcon from "@mui/icons-material/Image";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import LanguageIcon from "@mui/icons-material/Language";
import SaveIcon from "@mui/icons-material/Save";
import ResimUpload from "../ResimUpload";
import { API_BASE } from "../config";

const PLACEHOLDER = "Bu dilde içerik mevcut değildir.";
const ALLOWED = ["tr", "en", "de"];

const COLORS = {
  navy: "#071a33",
  navySoft: "#123c68",
  blue: "#2563eb",
  blueSoft: "#eff6ff",
  orange: "#f59e0b",
  orangeDark: "#d97706",
  orangeSoft: "#fff7ed",
  green: "#059669",
  greenSoft: "#ecfdf5",
  red: "#dc2626",
  redSoft: "#fff1f2",
  text: "#0f172a",
  muted: "#64748b",
  border: "#e2e8f0",
  page: "#f3f6fa",
};

const T = {
  tr: {
    listTitle: "Duyuru Yönetimi",
    listSubtitle:
      "Ana sayfanızdaki duyuru ve slayt içeriklerini tek merkezden yönetin.",
    newBtn: "Yeni Duyuru",
    exportBtn: "Dışa Aktar",
    save: "Kaydet",
    saving: "Kaydediliyor…",
    cancel: "Vazgeç",
    editTitle: "Duyuruyu Düzenle",
    editSubtitle: "İçeriği, görseli ve yayın durumunu güncelleyin.",
    createTitle: "Yeni Duyuru Oluştur",
    createSubtitle: "Kurumsal slayt alanınız için yeni bir içerik hazırlayın.",
    updatedOk: "✅ Duyuru güncellendi",
    createdOk: "✅ Duyuru başarıyla oluşturuldu",
    deletedOk: "✅ Duyuru silindi",
    deletedErr: "❌ Duyuru silinemedi",
    updateErr: "❌ Güncelleme hatası",
    uploadImage: "Görsel Yükle",
    image: "Görsel",
    title: "Başlık",
    description: "Açıklama",
    link: "Bağlantı",
    linkLbl: "Bağlantı (https://...)",
    imageUrl: "Görsel URL",
    status: "Durum",
    contentLang: "İçerik Dili",
    date: "Yayın Tarihi",
    edit: "Düzenle",
    delete: "Sil",
    confirmDelete: "Bu duyuruyu silmek istediğinize emin misiniz?",
    active: "Aktif",
    passive: "Pasif",
    total: "Toplam Duyuru",
    visibleActive: "Görünen Aktif",
    languages: "İçerik Dili",
    contentSection: "Duyuru İçeriği",
    contentSectionDesc: "Yayın dilini seçerek başlık ve açıklamayı hazırlayın.",
    visualSection: "Görsel ve Bağlantı",
    visualSectionDesc:
      "Slayt görselini yükleyin ve isteğe bağlı bağlantıyı ekleyin.",
    publishSection: "Yayın Ayarları",
    publishSectionDesc: "Tarih ve görünürlük durumunu belirleyin.",
    imageHelp: "Önerilen oran 16:9 · JPG, PNG veya WebP",
    noImage: "Görsel eklenmedi",
    noLink: "Bağlantı eklenmedi",
    loading: "Duyurular yükleniyor…",
    emptyTitle: "Henüz duyuru eklenmedi",
    emptyDesc:
      "İlk kurumsal duyurunuzu hazırlayarak ana sayfa slaytınızı yayınlayın.",
    emptyBtn: "İlk Duyuruyu Ekle",
    trTitle: "Başlık (Türkçe)",
    enTitle: "Title (English)",
    deTitle: "Titel (Deutsch)",
    trDesc: "Açıklama (Türkçe)",
    enDesc: "Description (English)",
    deDesc: "Beschreibung (Deutsch)",
    langNames: {
      tr: "Türkçe",
      en: "İngilizce",
      de: "Almanca",
    },
  },

  en: {
    listTitle: "Announcement Management",
    listSubtitle:
      "Manage homepage announcements and slider content centrally.",
    newBtn: "New Announcement",
    exportBtn: "Export",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    editTitle: "Edit Announcement",
    editSubtitle: "Update content, image and publication status.",
    createTitle: "Create Announcement",
    createSubtitle: "Prepare new content for your corporate slider.",
    updatedOk: "✅ Announcement updated",
    createdOk: "✅ Announcement created",
    deletedOk: "✅ Announcement deleted",
    deletedErr: "❌ Announcement could not be deleted",
    updateErr: "❌ Update error",
    uploadImage: "Upload Image",
    image: "Image",
    title: "Title",
    description: "Description",
    link: "Link",
    linkLbl: "Link (https://...)",
    imageUrl: "Image URL",
    status: "Status",
    contentLang: "Content Language",
    date: "Publication Date",
    edit: "Edit",
    delete: "Delete",
    confirmDelete:
      "Are you sure you want to delete this announcement?",
    active: "Active",
    passive: "Inactive",
    total: "Total Announcements",
    visibleActive: "Visible Active",
    languages: "Content Languages",
    contentSection: "Announcement Content",
    contentSectionDesc:
      "Choose a language and prepare the title and description.",
    visualSection: "Image and Link",
    visualSectionDesc:
      "Upload the slider image and add an optional link.",
    publishSection: "Publication Settings",
    publishSectionDesc: "Set the date and visibility status.",
    imageHelp: "Recommended ratio 16:9 · JPG, PNG or WebP",
    noImage: "No image added",
    noLink: "No link added",
    loading: "Loading announcements…",
    emptyTitle: "No announcements yet",
    emptyDesc:
      "Create your first corporate announcement for the homepage slider.",
    emptyBtn: "Add First Announcement",
    trTitle: "Title (Turkish)",
    enTitle: "Title (English)",
    deTitle: "Title (German)",
    trDesc: "Description (Turkish)",
    enDesc: "Description (English)",
    deDesc: "Description (German)",
    langNames: {
      tr: "Turkish",
      en: "English",
      de: "German",
    },
  },

  de: {
    listTitle: "Mitteilungsverwaltung",
    listSubtitle:
      "Verwalten Sie Mitteilungen und Slider-Inhalte zentral.",
    newBtn: "Neue Mitteilung",
    exportBtn: "Exportieren",
    save: "Speichern",
    saving: "Wird gespeichert…",
    cancel: "Abbrechen",
    editTitle: "Mitteilung bearbeiten",
    editSubtitle:
      "Inhalt, Bild und Veröffentlichungsstatus aktualisieren.",
    createTitle: "Mitteilung erstellen",
    createSubtitle:
      "Erstellen Sie einen neuen Inhalt für Ihren Slider.",
    updatedOk: "✅ Mitteilung aktualisiert",
    createdOk: "✅ Mitteilung erstellt",
    deletedOk: "✅ Mitteilung gelöscht",
    deletedErr: "❌ Mitteilung konnte nicht gelöscht werden",
    updateErr: "❌ Aktualisierungsfehler",
    uploadImage: "Bild hochladen",
    image: "Bild",
    title: "Titel",
    description: "Beschreibung",
    link: "Link",
    linkLbl: "Link (https://...)",
    imageUrl: "Bild-URL",
    status: "Status",
    contentLang: "Inhaltssprache",
    date: "Veröffentlichungsdatum",
    edit: "Bearbeiten",
    delete: "Löschen",
    confirmDelete:
      "Möchten Sie diese Mitteilung wirklich löschen?",
    active: "Aktiv",
    passive: "Inaktiv",
    total: "Mitteilungen gesamt",
    visibleActive: "Sichtbar aktiv",
    languages: "Inhaltssprachen",
    contentSection: "Inhalt der Mitteilung",
    contentSectionDesc:
      "Sprache wählen und Titel sowie Beschreibung eingeben.",
    visualSection: "Bild und Link",
    visualSectionDesc:
      "Slider-Bild hochladen und optionalen Link hinzufügen.",
    publishSection: "Veröffentlichung",
    publishSectionDesc: "Datum und Sichtbarkeit festlegen.",
    imageHelp:
      "Empfohlenes Verhältnis 16:9 · JPG, PNG oder WebP",
    noImage: "Kein Bild hinzugefügt",
    noLink: "Kein Link hinzugefügt",
    loading: "Mitteilungen werden geladen…",
    emptyTitle: "Noch keine Mitteilungen",
    emptyDesc:
      "Erstellen Sie die erste Mitteilung für Ihren Startseiten-Slider.",
    emptyBtn: "Erste Mitteilung hinzufügen",
    trTitle: "Titel (Türkisch)",
    enTitle: "Titel (Englisch)",
    deTitle: "Titel (Deutsch)",
    trDesc: "Beschreibung (Türkisch)",
    enDesc: "Beschreibung (Englisch)",
    deDesc: "Beschreibung (Deutsch)",
    langNames: {
      tr: "Türkisch",
      en: "Englisch",
      de: "Deutsch",
    },
  },
};

const pageGlobalStyles = {
  html: {
    overflowX: "hidden",
  },

  body: {
    margin: 0,
    overflowX: "hidden",
    background: COLORS.page,
  },

  "*": {
    boxSizing: "border-box",
  },

  "@keyframes duyuruFadeUp": {
    from: {
      opacity: 0,
      transform: "translateY(14px)",
    },

    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  "@keyframes duyuruGlow": {
    "0%, 100%": {
      opacity: 0.35,
      transform: "scale(1)",
    },

    "50%": {
      opacity: 0.6,
      transform: "scale(1.08)",
    },
  },
};

const fieldSx = {
  width: "100%",

  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    bgcolor: "#fff",
    transition: "box-shadow .2s ease",

    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },

    "&.Mui-focused": {
      boxShadow: "0 0 0 4px rgba(37,99,235,.09)",
    },
  },

  "& .MuiInputLabel-root": {
    fontWeight: 700,
  },
};

function useUiTexts() {
  const [uiLang, setUiLang] = useState(() =>
    (localStorage.getItem("app_lang") || "tr")
      .slice(0, 2)
      .toLowerCase(),
  );

  useEffect(() => {
    const sync = () => {
      const next = (
        localStorage.getItem("app_lang") || "tr"
      )
        .slice(0, 2)
        .toLowerCase();

      setUiLang(
        ALLOWED.includes(next) ? next : "tr",
      );
    };

    window.addEventListener(
      "app_lang_change",
      sync,
    );
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);

    return () => {
      window.removeEventListener(
        "app_lang_change",
        sync,
      );
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  return useMemo(
    () =>
      T[
        ALLOWED.includes(uiLang)
          ? uiLang
          : "tr"
      ],
    [uiLang],
  );
}

const cleanValue = (value) => {
  const result = String(value || "").trim();

  return result && result !== PLACEHOLDER
    ? result
    : "";
};

const recordLanguage = (record = {}) => {
  const selected = String(
    record.dil || "",
  ).toLowerCase();

  if (ALLOWED.includes(selected)) {
    return selected;
  }

  if (cleanValue(record.baslik_tr)) {
    return "tr";
  }

  if (cleanValue(record.baslik_en)) {
    return "en";
  }

  if (cleanValue(record.baslik_de)) {
    return "de";
  }

  return "tr";
};

const recordTitle = (record = {}) => {
  const preferred = recordLanguage(record);

  const order = [
    ...new Set([
      preferred,
      "tr",
      "en",
      "de",
    ]),
  ];

  for (const code of order) {
    const value = cleanValue(
      record[`baslik_${code}`],
    );

    if (value) {
      return value;
    }
  }

  return "—";
};

const recordDescription = (record = {}) => {
  const preferred = recordLanguage(record);

  const order = [
    ...new Set([
      preferred,
      "tr",
      "en",
      "de",
    ]),
  ];

  for (const code of order) {
    const value = cleanValue(
      record[`aciklama_${code}`],
    );

    if (value) {
      return value;
    }
  }

  return "";
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const inputDate = (value) => {
  if (!value) {
    return "";
  }

  const raw = String(value);

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? ""
    : date.toISOString().slice(0, 10);
};

function MetricCard({
  icon,
  value,
  label,
  accent,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        p: {
          xs: 1.25,
          sm: 1.6,
        },
        borderRadius: 3,
        color: "#fff",
        bgcolor: "rgba(255,255,255,.1)",
        border:
          "1px solid rgba(255,255,255,.15)",
        backdropFilter: "blur(12px)",
        transition:
          "transform .2s ease, background .2s ease",

        "&:hover": {
          transform: "translateY(-3px)",
          bgcolor: "rgba(255,255,255,.14)",
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.2}
        alignItems="center"
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: 2.25,
            display: "grid",
            placeItems: "center",
            bgcolor: `${accent}28`,
            color: accent,
          }}
        >
          {icon}
        </Box>

        <Box minWidth={0}>
          <Typography
            sx={{
              fontSize: "1.35rem",
              lineHeight: 1,
              fontWeight: 950,
            }}
          >
            {value}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              mt: 0.45,
              display: "block",
              color:
                "rgba(255,255,255,.72)",
              fontWeight: 750,
            }}
          >
            {label}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function ListActions({ TT }) {
  return (
    <TopToolbar
      sx={{
        width: "100%",
        px: {
          xs: 1,
          sm: 2,
          md: 3,
        },
        py: 1.25,
        gap: 1,
        justifyContent: "flex-end",
        flexWrap: "wrap",
        bgcolor: COLORS.page,
      }}
    >
      <CreateButton
        icon={<AddCircleIcon />}
        label={TT.newBtn}
        sx={{
          flex: {
            xs: "1 1 150px",
            sm: "0 0 auto",
          },
          minHeight: 44,
          px: 2.5,
          borderRadius: 2.5,
          textTransform: "none",
          fontWeight: 900,
          bgcolor: `${COLORS.orange} !important`,
          color: "#fff !important",
          boxShadow:
            "0 8px 20px rgba(245,158,11,.22)",

          "&:hover": {
            bgcolor: `${COLORS.orangeDark} !important`,
          },
        }}
      />

      <ExportButton
        icon={<FileDownloadIcon />}
        label={TT.exportBtn}
        sx={{
          flex: {
            xs: "1 1 130px",
            sm: "0 0 auto",
          },
          minHeight: 44,
          px: 2.25,
          borderRadius: 2.5,
          textTransform: "none",
          fontWeight: 850,
          bgcolor: `${COLORS.blue} !important`,
          color: "#fff !important",
          boxShadow:
            "0 8px 20px rgba(37,99,235,.18)",

          "&:hover": {
            bgcolor: "#1d4ed8 !important",
          },
        }}
      />
    </TopToolbar>
  );
}

function CorporateListHeader({
  TT,
  total,
  activeCount,
}) {
  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        overflow: "hidden",
        color: "#fff",
        background:
          "linear-gradient(125deg, #06162c 0%, #123f70 55%, #075985 100%)",
        borderBottom: `4px solid ${COLORS.orange}`,

        "&::before": {
          content: '""',
          position: "absolute",
          width: {
            xs: 260,
            md: 470,
          },
          height: {
            xs: 260,
            md: 470,
          },
          right: {
            xs: -155,
            md: -140,
          },
          top: {
            xs: -150,
            md: -255,
          },
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,.19), transparent 68%)",
          animation:
            "duyuruGlow 5s ease-in-out infinite",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          p: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        }}
      >
        <Stack
          direction={{
            xs: "column",
            lg: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            lg: "flex-end",
          }}
          spacing={3}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Chip
              icon={<CampaignIcon />}
              label={TT.contentSection}
              sx={{
                mb: 1.5,
                color: "#fff",
                fontWeight: 850,
                bgcolor:
                  "rgba(255,255,255,.1)",
                border:
                  "1px solid rgba(255,255,255,.16)",

                "& .MuiChip-icon": {
                  color: "#fbbf24",
                },
              }}
            />

            <Typography
              component="h1"
              sx={{
                fontSize: {
                  xs: "1.85rem",
                  sm: "2.45rem",
                  md: "3rem",
                },
                lineHeight: 1.07,
                letterSpacing: {
                  xs: -0.5,
                  md: -1.2,
                },
                fontWeight: 950,
              }}
            >
              {TT.listTitle}
            </Typography>

            <Typography
              sx={{
                mt: 1,
                maxWidth: 680,
                color:
                  "rgba(255,255,255,.75)",
                lineHeight: 1.65,
              }}
            >
              {TT.listSubtitle}
            </Typography>
                   </Box>
        </Stack>

        <Box
          sx={{
            mt: 3,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, minmax(0, 1fr))",
            },
            gap: 1.25,
          }}
        >
          <MetricCard
            icon={<CampaignIcon />}
            value={total}
            label={TT.total}
            accent="#67e8f9"
          />

          <MetricCard
            icon={<CheckCircleIcon />}
            value={activeCount}
            label={TT.visibleActive}
            accent="#86efac"
          />

          <MetricCard
            icon={<LanguageIcon />}
            value="TR · EN · DE"
            label={TT.languages}
            accent="#fbbf24"
          />
        </Box>
      </Box>
    </Box>
  );
}

function CustomDeleteButton({
  record,
  compact = false,
}) {
  const TT = useUiTexts();
  const refresh = useRefresh();
  const notify = useNotify();

  const handleDelete = async (event) => {
    event?.stopPropagation?.();

    if (!window.confirm(TT.confirmDelete)) {
      return;
    }

    try {
      const token =
        localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE}/duyurular/${record.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}`,
        );
      }

      notify(TT.deletedOk, {
        type: "success",
      });

      refresh();
      window.location.hash = "#/duyurular";
    } catch (error) {
      console.error(
        "Duyuru silme hatası:",
        error,
      );

      notify(TT.deletedErr, {
        type: "error",
      });
    }
  };

  return (
    <Button
      type="button"
      size={compact ? "small" : "medium"}
      onClick={handleDelete}
      startIcon={<DeleteIcon />}
      sx={{
        minHeight: compact ? 36 : 40,
        px: compact ? 1.3 : 1.7,
        borderRadius: 2.25,
        textTransform: "none",
        fontWeight: 850,
        color: COLORS.red,
        bgcolor: COLORS.redSoft,
        border: "1px solid #fecdd3",

        "&:hover": {
          bgcolor: "#ffe4e6",
        },
      }}
    >
      {TT.delete}
    </Button>
  );
}

function ActionButtons() {
  const TT = useUiTexts();
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={0.8}
      justifyContent="flex-end"
    >
      <EditButton
        record={record}
        icon={<EditIcon />}
        label={TT.edit}
        onClick={(event) =>
          event.stopPropagation()
        }
        sx={{
          minHeight: 40,
          px: 1.7,
          borderRadius: 2.25,
          textTransform: "none",
          fontWeight: 850,
          color: "#9a3412 !important",
          bgcolor: `${COLORS.orangeSoft} !important`,
          border: "1px solid #fed7aa",

          "&:hover": {
            bgcolor: "#ffedd5 !important",
          },
        }}
      />

      <CustomDeleteButton
        record={record}
        compact
      />
    </Stack>
  );
}

function ImageCell() {
  const record = useRecordContext();
  const TT = useUiTexts();

  if (!record) {
    return null;
  }

  return record.resim ? (
    <Box
      component="img"
      src={record.resim}
      alt={recordTitle(record)}
      sx={{
        width: 84,
        height: 58,
        display: "block",
        objectFit: "cover",
        borderRadius: 2,
        border: `1px solid ${COLORS.border}`,
      }}
    />
  ) : (
    <Box
      title={TT.noImage}
      sx={{
        width: 84,
        height: 58,
        display: "grid",
        placeItems: "center",
        borderRadius: 2,
        color: "#94a3b8",
        bgcolor: "#f1f5f9",
        border: "1px dashed #cbd5e1",
      }}
    >
      <ImageIcon />
    </Box>
  );
}

function TitleCell() {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  const title = recordTitle(record);

  const description =
    recordDescription(record);

  return (
    <Box minWidth={0}>
      <Typography
        title={title}
        sx={{
          fontWeight: 900,
          color: COLORS.text,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.35,
            display: "block",
            color: COLORS.muted,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
}

function LinkCell() {
  const record = useRecordContext();
  const TT = useUiTexts();

  if (!record) {
    return null;
  }

  if (!record.link) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
      >
        {TT.noLink}
      </Typography>
    );
  }

  return (
    <Tooltip title={record.link}>
      <Typography
        component="a"
        href={record.link}
        target="_blank"
        rel="noreferrer"
        onClick={(event) =>
          event.stopPropagation()
        }
        sx={{
          display: "block",
          color: COLORS.blue,
          fontWeight: 750,
          textDecoration: "none",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",

          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        {record.link}
      </Typography>
    </Tooltip>
  );
}

function LanguageCell() {
  const record = useRecordContext();
  const TT = useUiTexts();

  if (!record) {
    return null;
  }

  const code = recordLanguage(record);

  return (
    <Chip
      size="small"
      icon={<LanguageIcon />}
      label={
        TT.langNames[code] ||
        code.toUpperCase()
      }
      sx={{
        fontWeight: 850,
        color: COLORS.blue,
        bgcolor: COLORS.blueSoft,

        "& .MuiChip-icon": {
          color: COLORS.blue,
        },
      }}
    />
  );
}

function StatusCell() {
  const record = useRecordContext();
  const TT = useUiTexts();

  if (!record) {
    return null;
  }

  return (
    <Chip
      size="small"
      icon={<CheckCircleIcon />}
      label={
        record.aktif
          ? TT.active
          : TT.passive
      }
      sx={{
        fontWeight: 850,
        color: record.aktif
          ? COLORS.green
          : COLORS.muted,
        bgcolor: record.aktif
          ? COLORS.greenSoft
          : "#f1f5f9",

        "& .MuiChip-icon": {
          color: record.aktif
            ? COLORS.green
            : "#94a3b8",
        },
      }}
    />
  );
}

function DateCell() {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <Typography
      sx={{
        color: "#334155",
        fontWeight: 750,
        whiteSpace: "nowrap",
      }}
    >
      {formatDate(record.tarih)}
    </Typography>
  );
}

function EmptyState({ TT }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        p: {
          xs: 3,
          md: 6,
        },
        borderRadius: 4,
        borderColor: COLORS.border,
        textAlign: "center",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          width: 84,
          height: 84,
          mx: "auto",
          display: "grid",
          placeItems: "center",
          borderRadius: 4,
          color: COLORS.blue,
          bgcolor: COLORS.blueSoft,
        }}
      >
        <ImageIcon sx={{ fontSize: 40 }} />
      </Box>

      <Typography
        component="h2"
        sx={{
          mt: 2,
          fontSize: {
            xs: "1.25rem",
            md: "1.6rem",
          },
          color: COLORS.text,
          fontWeight: 950,
        }}
      >
        {TT.emptyTitle}
      </Typography>

      <Typography
        sx={{
          mt: 0.75,
          mx: "auto",
          maxWidth: 620,
          color: COLORS.muted,
          lineHeight: 1.65,
        }}
      >
        {TT.emptyDesc}
      </Typography>

      <CreateButton
        icon={<AddCircleIcon />}
        label={TT.emptyBtn}
        sx={{
          mt: 2.25,
          minHeight: 46,
          px: 2.5,
          borderRadius: 2.5,
          textTransform: "none",
          fontWeight: 900,
          color: "#fff !important",
          bgcolor: `${COLORS.orange} !important`,

          "&:hover": {
            bgcolor: `${COLORS.orangeDark} !important`,
          },
        }}
      />
    </Paper>
  );
}

function MobileAnnouncementCard({
  record,
}) {
  const TT = useUiTexts();
  const redirect = useRedirect();

  const title = recordTitle(record);

  const description =
    recordDescription(record);

  const code = recordLanguage(record);

  return (
    <Paper
      variant="outlined"
      sx={{
        minWidth: 0,
        overflow: "hidden",
        borderRadius: 3.5,
        borderColor: COLORS.border,
        bgcolor: "#fff",
        boxShadow:
          "0 12px 30px rgba(15,23,42,.055)",
        animation:
          "duyuruFadeUp .35s ease both",
      }}
    >
      <Box
        sx={{
          position: "relative",
          aspectRatio: "16 / 8.5",
          bgcolor: "#e2e8f0",
        }}
      >
        {record.resim ? (
          <Box
            component="img"
            src={record.resim}
            alt={title}
            sx={{
              width: "100%",
              height: "100%",
              display: "block",
              objectFit: "cover",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "grid",
              placeItems: "center",
              color: "#94a3b8",
              background:
                "linear-gradient(135deg,#f8fafc,#e2e8f0)",
            }}
          >
            <ImageIcon
              sx={{ fontSize: 46 }}
            />
          </Box>
        )}

        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            right: 10,
            justifyContent: "space-between",
          }}
        >
          <Chip
            size="small"
            label={TT.langNames[code]}
            sx={{
              fontWeight: 850,
              bgcolor:
                "rgba(255,255,255,.92)",
              backdropFilter: "blur(8px)",
            }}
          />

          <Chip
            size="small"
            label={
              record.aktif
                ? TT.active
                : TT.passive
            }
            sx={{
              fontWeight: 850,
              color: record.aktif
                ? COLORS.green
                : COLORS.muted,
              bgcolor:
                "rgba(255,255,255,.92)",
              backdropFilter: "blur(8px)",
            }}
          />
        </Stack>
      </Box>

      <Box sx={{ p: 1.75 }}>
        <Typography
                  sx={{
            width: "100%",
            minWidth: 0,
            fontSize: "1.06rem",
            color: COLORS.text,
            fontWeight: 950,
            lineHeight: 1.35,
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {title}
        </Typography>

        {description && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.7,
              color: COLORS.muted,
              lineHeight: 1.55,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {description}
          </Typography>
        )}

        <Divider sx={{ my: 1.4 }} />

        <Stack spacing={0.8}>
          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
          >
            <CalendarMonthIcon
              sx={{
                fontSize: 18,
                color: COLORS.muted,
              }}
            />

            <Typography
              variant="body2"
              sx={{
                color: "#334155",
                fontWeight: 750,
              }}
            >
              {formatDate(record.tarih)}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
            minWidth={0}
          >
            <InsertLinkIcon
              sx={{
                fontSize: 18,
                color: COLORS.muted,
                flexShrink: 0,
              }}
            />

            <Typography
              variant="body2"
              sx={{
                minWidth: 0,
                color: record.link
                  ? COLORS.blue
                  : COLORS.muted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {record.link || TT.noLink}
            </Typography>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 1.6 }}
        >
          <Button
            fullWidth
            type="button"
            startIcon={<EditIcon />}
            onClick={() =>
              redirect(
                `/duyurular/${record.id}`,
              )
            }
            sx={{
              minHeight: 42,
              borderRadius: 2.25,
              textTransform: "none",
              fontWeight: 850,
              color: "#9a3412",
              bgcolor: COLORS.orangeSoft,
              border: "1px solid #fed7aa",
            }}
          >
            {TT.edit}
          </Button>

          <CustomDeleteButton
            record={record}
            compact
          />
        </Stack>
      </Box>
    </Paper>
  );
}

function DuyuruListContent() {
  const TT = useUiTexts();

  const {
    data,
    total,
    isPending,
    isLoading,
  } = useListContext();

  const records = Array.isArray(data)
    ? data
    : data
      ? Object.values(data)
      : [];

  const loading = Boolean(
    isPending || isLoading,
  );

  const activeCount = records.filter(
    (item) => item?.aktif,
  ).length;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        bgcolor: COLORS.page,
        pb: 4,
      }}
    >
      <GlobalStyles
        styles={pageGlobalStyles}
      />

      <CorporateListHeader
        TT={TT}
        total={total ?? records.length}
        activeCount={activeCount}
      />

      <Box
        sx={{
          width: "100%",
          px: {
            xs: 1,
            sm: 2,
            md: 3,
          },
          pt: {
            xs: 1.5,
            md: 2.5,
          },
        }}
      >
        {loading ? (
          <Paper
            sx={{
              minHeight: 280,
              display: "grid",
              placeItems: "center",
              borderRadius: 4,
            }}
          >
            <Stack
              alignItems="center"
              spacing={1.5}
            >
              <CircularProgress />

              <Typography
                sx={{
                  color: COLORS.muted,
                  fontWeight: 750,
                }}
              >
                {TT.loading}
              </Typography>
            </Stack>
          </Paper>
        ) : records.length === 0 ? (
          <EmptyState TT={TT} />
        ) : (
          <>
          


  <Paper
  variant="outlined"
  sx={{
    display: {
      xs: "none",
      lg: "block",
    },
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    borderRadius: 4,
    borderColor: COLORS.border,
    boxShadow:
      "0 14px 38px rgba(15,23,42,.055)",
    animation:
      "duyuruFadeUp .35s ease both",

    "&::-webkit-scrollbar": {
      height: 8,
    },

    "&::-webkit-scrollbar-thumb": {
      borderRadius: 8,
      bgcolor: "#cbd5e1",
    },
  }}
>
  <Datagrid
    rowClick="edit"
    bulkActionButtons={false}
    sx={{
      width: "100%",
      maxWidth: "100%",
      minWidth: "100%",
      tableLayout: "fixed",

      "& .MuiTable-root": {
        width: "100%",
        maxWidth: "100%",
        tableLayout: "fixed",
      },

      "& .RaDatagrid-headerCell": {
        py: 1.5,
        color: "#475569",
        bgcolor: "#f8fafc",
        fontSize: ".78rem",
        letterSpacing: ".035em",
        textTransform: "uppercase",
        fontWeight: 900,
        borderBottom: `1px solid ${COLORS.border}`,
      },

      "& .RaDatagrid-row": {
        transition: "background .18s ease",

        "&:hover": {
          bgcolor: "#f8fbff",
        },
      },

      "& .MuiTableCell-root": {
        minWidth: 0,
        px: 1.25,
        py: 1.15,
        overflow: "hidden",
        verticalAlign: "middle",
        borderColor: "#edf2f7",
      },

      "& .column-image, & th:nth-of-type(1), & td:nth-of-type(1)": {
        width: 105,
        minWidth: 105,
        maxWidth: 105,
      },

      "& .column-title, & th:nth-of-type(2), & td:nth-of-type(2)": {
        width: "auto",
        minWidth: 0,
      },

      "& .column-link, & th:nth-of-type(3), & td:nth-of-type(3)": {
        width: "20%",
      },

      "& .column-language, & th:nth-of-type(4), & td:nth-of-type(4)": {
        width: 125,
        minWidth: 125,
        maxWidth: 125,
      },

      "& .column-status, & th:nth-of-type(5), & td:nth-of-type(5)": {
        width: 105,
        minWidth: 105,
        maxWidth: 105,
      },

      "& .column-date, & th:nth-of-type(6), & td:nth-of-type(6)": {
        width: 120,
        minWidth: 120,
        maxWidth: 120,
      },

      "& .column-actions, & th:last-of-type, & td:last-of-type": {
        position: "sticky",
        right: 0,
        zIndex: 4,
        width: 205,
        minWidth: 205,
        maxWidth: 205,
        bgcolor: "#fff",
        boxShadow:
          "-8px 0 14px rgba(15,23,42,.08)",
      },

      "& thead th:last-of-type": {
        zIndex: 7,
        bgcolor: "#f8fafc",
      },

      "& .column-actions .MuiStack-root, & td:last-of-type .MuiStack-root": {
        width: "100%",
        justifyContent: "flex-end",
        flexWrap: "nowrap",
        gap: 0.5,
      },

      "& .column-actions .MuiButton-root, & td:last-of-type .MuiButton-root": {
        minWidth: 0,
        px: 1.15,
      },

      "@media (max-width: 1599px)": {
        "& .column-link, & th:nth-of-type(3), & td:nth-of-type(3)": {
          display: "none",
        },
      },

      "@media (max-width: 1399px)": {
        "& .column-language, & th:nth-of-type(4), & td:nth-of-type(4)": {
          display: "none",
        },
      },
    }}
  >





                <ImageCell
                  label={TT.image}
                  className="column-image"
                />

                <TitleCell
                  label={TT.title}
                  className="column-title"
                />

                <LinkCell
                  label={TT.link}
                  className="column-link"
                />

                <LanguageCell
                  label={TT.contentLang}
                  className="column-language"
                />

                <StatusCell
                  label={TT.status}
                  className="column-status"
                />

                <DateCell
                  label={TT.date}
                  className="column-date"
                />

                <ActionButtons
                  label=""
                  className="column-actions"
                />
              </Datagrid>
            </Paper>

          <Box
  sx={{
    display: {
      xs: "grid",
      lg: "none",
    },
                gridTemplateColumns: {
                  xs: "minmax(0,1fr)",
                  sm: "repeat(2,minmax(0,1fr))",
                },
                gap: 1.25,
              }}
            >
              {records.map((record) => (
                <MobileAnnouncementCard
                  key={record.id}
                  record={record}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export function DuyurularPage(props) {
  const TT = useUiTexts();

  return (
    <List
      {...props}
      title={TT.listTitle}
      actions={<ListActions TT={TT} />}
      empty={false}
      sort={{
        field: "tarih",
        order: "DESC",
      }}
      sx={{
        width: "100%",
        maxWidth: "100%",
        m: 0,
        p: 0,

        "& .RaList-main": {
          width: "100%",
          maxWidth: "100%",
        },

        "& .RaList-content": {
          width: "100%",
          maxWidth: "100%",
          p: 0,
          m: 0,
          boxShadow: "none",
          borderRadius: 0,
          bgcolor: "transparent",
        },

        "& .MuiCard-root": {
          maxWidth: "100%",
        },

        "& .MuiContainer-root": {
          maxWidth: "100% !important",
          px: "0 !important",
        },

        "& .RaList-pagination": {
          px: {
            xs: 1,
            md: 3,
          },
          bgcolor: COLORS.page,
        },
      }}
    >
      <DuyuruListContent />
    </List>
  );
}

function SectionCard({
  icon,
  title,
  description,
  accent = COLORS.blue,
  children,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: 0,
        p: {
          xs: 1.5,
          sm: 2.25,
          md: 2.75,
        },
        borderRadius: {
          xs: 3,
          md: 4,
        },
        borderColor: COLORS.border,
        bgcolor: "#fff",
        boxShadow:
          "0 14px 36px rgba(15,23,42,.05)",
        animation:
          "duyuruFadeUp .35s ease both",
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ mb: 2.25 }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            borderRadius: 2.5,
            color: accent,
            bgcolor: `${accent}12`,
          }}
        >
          {icon}
        </Box>

        <Box minWidth={0}>
          <Typography
            component="h2"
            sx={{
              color: COLORS.text,
              fontSize: {
                xs: "1rem",
                sm: "1.15rem",
              },
              lineHeight: 1.25,
              fontWeight: 950,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.3,
              color: COLORS.muted,
              lineHeight: 1.5,
            }}
          >
            {description}
          </Typography>
        </Box>
      </Stack>

      {children}
    </Paper>
  );
}

function FormHero({ TT, mode }) {
  const title =
    mode === "edit"
      ? TT.editTitle
      : TT.createTitle;

  const subtitle =
    mode === "edit"
      ? TT.editSubtitle
      : TT.createSubtitle;

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        overflow: "hidden",
        color: "#fff",
        background:
          "linear-gradient(125deg,#06162c 0%,#123f70 55%,#075985 100%)",
        borderBottom: `4px solid ${COLORS.orange}`,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 330,
          height: 330,
          right: -120,
          top: -210,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(255,255,255,.18),transparent 68%)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          p: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        }}
      >
        <Chip
          icon={
            mode === "edit" ? (
              <EditIcon />
            ) : (
              <AddCircleIcon />
            )
          }
          label={
            mode === "edit"
              ? TT.edit
              : TT.newBtn
          }
          sx={{
            mb: 1.5,
            color: "#fff",
            fontWeight: 850,
            bgcolor:
              "rgba(255,255,255,.1)",
            border:
              "1px solid rgba(255,255,255,.16)",

            "& .MuiChip-icon": {
              color: "#fbbf24",
            },
          }}
        />

        <Typography
          component="h1"
          sx={{
            fontSize: {
              xs: "1.75rem",
              sm: "2.35rem",
              md: "2.8rem",
            },
            lineHeight: 1.08,
            fontWeight: 950,
            letterSpacing: {
              xs: -0.4,
              md: -1,
            },
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            mt: 0.9,
            maxWidth: 720,
            color:
              "rgba(255,255,255,.75)",
            lineHeight: 1.65,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}

function LangAwareInputs({ TT }) {
  const dil = String(
    useWatch({ name: "dil" }) || "tr",
  ).toLowerCase();

  const fields = [
    {
      code: "tr",
      title: TT.trTitle,
      description: TT.trDesc,
    },
    {
      code: "en",
      title: TT.enTitle,
      description: TT.enDesc,
    },
    {
      code: "de",
      title: TT.deTitle,
      description: TT.deDesc,
    },
  ];

  return (
    <>
      {fields.map((item) => (
        <Box
          key={item.code}
          sx={{
            display:
              dil === item.code
                ? "grid"
                : "none",
            gap: 1.5,
          }}
        >
          <TextInput
            source={`baslik_${item.code}`}
            label={item.title}
            fullWidth
            sx={fieldSx}
          />

          <TextInput
            source={`aciklama_${item.code}`}
            label={item.description}
            fullWidth
            multiline
            minRows={5}
            sx={fieldSx}
          />
        </Box>
      ))}
    </>
  );
}

function ResimRow({ TT }) {
  const { setValue } = useFormContext();

  const rawUrl =
    useWatch({ name: "resim" }) || "";

  const [version, setVersion] =
    useState(0);

  const preview = rawUrl
    ? `${rawUrl}${
        rawUrl.includes("?") ? "&" : "?"
      }v=${version}`
    : "";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0,1fr)",
          md: "minmax(260px,4fr) minmax(0,8fr)",
        },
        gap: 2,
        alignItems: "stretch",
      }}
    >
      <Box
        sx={{
          minHeight: {
            xs: 190,
            md: 220,
          },
          overflow: "hidden",
          borderRadius: 3,
          border: `1px solid ${COLORS.border}`,
          bgcolor: "#f1f5f9",
          display: "grid",
          placeItems: "center",
        }}
      >
        {preview ? (
          <Box
            component="img"
            src={preview}
            alt="preview"
            sx={{
              width: "100%",
              height: "100%",
              minHeight: {
                xs: 190,
                md: 220,
              },
              display: "block",
              objectFit: "cover",
            }}
          />
        ) : (
          <Stack
            alignItems="center"
            spacing={0.75}
            sx={{ color: "#94a3b8" }}
          >
            <ImageIcon
              sx={{ fontSize: 44 }}
            />

            <Typography
              variant="body2"
              sx={{ fontWeight: 750 }}
            >
              {TT.noImage}
            </Typography>
          </Stack>
        )}
      </Box>

      <Stack
        spacing={1.5}
        justifyContent="center"
        minWidth={0}
      >
        <TextInput
          source="resim"
          label={TT.imageUrl}
          fullWidth
          sx={fieldSx}
        />

        <Box>
          <ResimUpload
            label={TT.uploadImage}
            defaultPreview={preview}
            onUpload={(url) => {
              setValue("resim", url, {
                shouldDirty: true,
                shouldTouch: true,
              });

              setVersion(
                (current) => current + 1,
              );
            }}
          />
        </Box>

        <Typography
          variant="caption"
          sx={{
            color: COLORS.muted,
            fontWeight: 700,
          }}
        >
          {TT.imageHelp}
        </Typography>
      </Stack>
    </Box>
  );
}

function CorporateToolbar({ TT }) {
  const redirect = useRedirect();

  return (
    <Toolbar
      sx={{
        position: "sticky",
        bottom: 8,
        zIndex: 10,
        width: "100%",
        mt: 0,
        p: {
          xs: 1.15,
          sm: 1.5,
        },
        borderRadius: 3,
        border: `1px solid ${COLORS.border}`,
        bgcolor:
          "rgba(255,255,255,.94)",
        backdropFilter: "blur(14px)",
        boxShadow:
          "0 16px 36px rgba(15,23,42,.13)",
      }}
    >
      <Stack
        direction={{
          xs: "column-reverse",
          sm: "row",
        }}
        spacing={1}
        justifyContent="flex-end"
        sx={{ width: "100%" }}
      >
        <Button
          type="button"
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            redirect("/duyurular")
          }
          sx={{
            minHeight: 48,
            px: 2.5,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 850,
            color: "#334155",
            bgcolor: "#f1f5f9",
          }}
        >
          {TT.cancel}
        </Button>

        <SaveButton
          icon={<SaveIcon />}
          label={TT.save}
          sx={{
            minHeight: 48,
            px: 3,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 900,
            color: "#fff !important",
            bgcolor: `${COLORS.blue} !important`,
            boxShadow:
              "0 10px 24px rgba(37,99,235,.22)",

            "&:hover": {
              bgcolor:
                "#1d4ed8 !important",
            },
          }}
        />
      </Stack>
    </Toolbar>
  );
}

const normalizeCreate = (data) => {
  const next = {
    ...data,
  };

  next.dil = ALLOWED.includes(
    String(next.dil || "").toLowerCase(),
  )
    ? String(next.dil).toLowerCase()
    : "tr";

  next.aktif = next.aktif !== false;

  if (next.tarih) {
    next.tarih = inputDate(next.tarih);
  }

  [
    "baslik_tr",
    "baslik_en",
    "baslik_de",
    "aciklama_tr",
    "aciklama_en",
    "aciklama_de",
  ].forEach((key) => {
    if (
      next[key] == null ||
      next[key] === PLACEHOLDER
    ) {
      next[key] = "";
    }
  });

  if (next.resim == null) {
    next.resim = "";
  }

  if (next.link == null) {
    next.link = "";
  }

  return next;
};

export function DuyuruCreate(props) {
  const TT = useUiTexts();
  const notify = useNotify();
  const redirect = useRedirect();

  return (
    <Create
      {...props}
      title={TT.createTitle}
      actions={false}
      transform={normalizeCreate}
      mutationOptions={{
        onSuccess: () => {
          notify(TT.createdOk, {
            type: "success",
          });

          redirect("/duyurular");
        },
      }}
      sx={{
        width: "100%",
        maxWidth: "100%",
        m: 0,

        "& .RaCreate-main": {
          width: "100%",
          maxWidth: "100%",
        },

        "& .RaCreate-card": {
          width: "100%",
          maxWidth: "100%",
          m: 0,
          p: 0,
          borderRadius: 0,
          boxShadow: "none",
          bgcolor: COLORS.page,
        },

        "& .MuiCard-root": {
          maxWidth: "100%",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <GlobalStyles
          styles={pageGlobalStyles}
        />

        <FormHero TT={TT} mode="create" />

        <SimpleForm
          defaultValues={{
            dil: "tr",
            aktif: true,
            tarih: new Date()
              .toISOString()
              .slice(0, 10),
            baslik_tr: "",
            baslik_en: "",
            baslik_de: "",
            aciklama_tr: "",
            aciklama_en: "",
            aciklama_de: "",
            resim: "",
            link: "",
          }}
          toolbar={
            <CorporateToolbar TT={TT} />
          }
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            p: {
              xs: 1,
              sm: 2,
              md: 3,
            },
            bgcolor: COLORS.page,

            "& > div": {
              width: "100%",
              maxWidth: "100%",
            },
          }}
        >
          <Stack
            spacing={{
              xs: 1.5,
              md: 2.25,
            }}
            sx={{ width: "100%" }}
          >
            <SectionCard
              icon={<CampaignIcon />}
              title={TT.contentSection}
              description={
                TT.contentSectionDesc
              }
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0,1fr)",
                    lg: "minmax(220px,3fr) minmax(0,9fr)",
                  },
                  gap: 1.5,
                  alignItems: "start",
                }}
              >
                <SelectInput
                  source="dil"
                  label={TT.contentLang}
                  choices={ALLOWED.map(
                    (code) => ({
                      id: code,
                      name:
                        TT.langNames[code],
                    }),
                  )}
                  fullWidth
                  sx={fieldSx}
                />

                <LangAwareInputs TT={TT} />
              </Box>
            </SectionCard>

            <SectionCard
              icon={<ImageIcon />}
              title={TT.visualSection}
              description={
                TT.visualSectionDesc
              }
              accent={COLORS.orange}
            >
              <ResimRow TT={TT} />

              <Box sx={{ mt: 1.5 }}>
                <TextInput
                  source="link"
                  label={TT.linkLbl}
                  fullWidth
                  sx={fieldSx}
                />
              </Box>
            </SectionCard>

            <SectionCard
              icon={<CalendarMonthIcon />}
              title={TT.publishSection}
              description={
                TT.publishSectionDesc
              }
              accent={COLORS.green}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0,1fr)",
                    md: "repeat(2,minmax(0,1fr))",
                  },
                  gap: 1.5,
                  alignItems: "center",
                }}
              >
                <DateInput
                  source="tarih"
                  label={TT.date}
                  fullWidth
                  sx={fieldSx}
                />

                <BooleanInput
                  source="aktif"
                  label={TT.status}
                  helperText={false}
                  sx={{
                    m: 0,
                    p: 1.25,
                    minHeight: 56,
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 2.5,
                    bgcolor:
                      COLORS.greenSoft,
                    border:
                      "1px solid #a7f3d0",

                    "& .MuiFormControlLabel-label":
                      {
                        color: COLORS.green,
                        fontWeight: 850,
                      },
                  }}
                />
              </Box>
            </SectionCard>
          </Stack>
        </SimpleForm>
      </Box>
    </Create>
  );
}

export function DuyuruEdit(props) {
  return (
    <EditBase
      resource="duyurular"
      {...props}
    >
      <DuyuruEditView />
    </EditBase>
  );
}

function DuyuruEditView() {
  const TT = useUiTexts();
  const notify = useNotify();
  const redirect = useRedirect();

  const { record, isLoading } =
    useEditContext();

  const [update, { isLoading: saving }] =
    useUpdate();

  const [form, setForm] = useState({
    baslik_tr: "",
    baslik_en: "",
    baslik_de: "",
    aciklama_tr: "",
    aciklama_en: "",
    aciklama_de: "",
    resim: "",
    link: "",
    aktif: true,
    dil: "tr",
    tarih: "",
  });

  useEffect(() => {
    if (!isLoading && record) {
      setForm({
        baslik_tr: cleanValue(
          record.baslik_tr,
        ),
        baslik_en: cleanValue(
          record.baslik_en,
        ),
        baslik_de: cleanValue(
          record.baslik_de,
        ),
        aciklama_tr: cleanValue(
          record.aciklama_tr,
        ),
        aciklama_en: cleanValue(
          record.aciklama_en,
        ),
        aciklama_de: cleanValue(
          record.aciklama_de,
        ),
        resim: record.resim || "",
        link: record.link || "",
        aktif: Boolean(record.aktif),
        dil: recordLanguage(record),
        tarih: inputDate(record.tarih),
      });
    }
  }, [isLoading, record]);

  const set = (patch) =>
    setForm((current) => ({
      ...current,
      ...patch,
    }));

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!record) {
      return;
    }

    const merged = {
      ...record,
      ...form,
      link:
        form.link ?? record.link ?? "",
      resim: form.resim || "",
      aktif: Boolean(form.aktif),
      dil: ALLOWED.includes(form.dil)
        ? form.dil
        : "tr",
      tarih: form.tarih
        ? inputDate(form.tarih)
        : record.tarih || "",
    };

    [
      "baslik_tr",
      "baslik_en",
      "baslik_de",
      "aciklama_tr",
      "aciklama_en",
      "aciklama_de",
    ].forEach((key) => {
      if (merged[key] === PLACEHOLDER) {
        merged[key] = "";
      }
    });

    update(
      "duyurular",
      {
        id: record.id,
        data: merged,
        previousData: record,
      },
      {
        onSuccess: () => {
          notify(TT.updatedOk, {
            type: "success",
          });

          redirect("/duyurular");
        },

        onError: (error) =>
          notify(
            `${TT.updateErr}: ${
              error?.message || ""
            }`,
            {
              type: "warning",
            },
          ),
      },
    );
  };

  if (isLoading || !record) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "grid",
          placeItems: "center",
          bgcolor: COLORS.page,
        }}
      >
        <Stack
          alignItems="center"
          spacing={1.5}
        >
          <CircularProgress />

          <Typography
            sx={{
              color: COLORS.muted,
              fontWeight: 750,
            }}
          >
            {TT.loading}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        bgcolor: COLORS.page,
        pb: 4,
      }}
    >
      <GlobalStyles
        styles={pageGlobalStyles}
      />

      <FormHero TT={TT} mode="edit" />

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          px: {
            xs: 1,
            sm: 2,
            md: 3,
          },
          pt: {
            xs: 1.5,
            md: 2.5,
          },
        }}
      >
        <Stack
          spacing={{
            xs: 1.5,
            md: 2.25,
          }}
        >
          <SectionCard
            icon={<CampaignIcon />}
            title={TT.contentSection}
            description={
              TT.contentSectionDesc
            }
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0,1fr)",
                  lg: "minmax(220px,3fr) minmax(0,9fr)",
                },
                gap: 1.5,
                alignItems: "start",
              }}
            >
              <TextField
                select
                fullWidth
                label={TT.contentLang}
                value={form.dil}
                onChange={(event) =>
                  set({
                    dil: event.target.value,
                  })
                }
                sx={fieldSx}
              >
                {ALLOWED.map((code) => (
                  <MenuItem
                    key={code}
                    value={code}
                  >
                    {TT.langNames[code]}
                  </MenuItem>
                ))}
              </TextField>

              <Stack
                spacing={1.5}
                minWidth={0}
              >
                <TextField
                  fullWidth
                  required
                  label={TT.title}
                  value={
                    form[
                      `baslik_${form.dil}`
                    ] || ""
                  }
                  onChange={(event) =>
                    set({
                      [`baslik_${form.dil}`]:
                        event.target.value,
                    })
                  }
                  sx={fieldSx}
                />

                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={5}
                  label={TT.description}
                  value={
                    form[
                      `aciklama_${form.dil}`
                    ] || ""
                  }
                  onChange={(event) =>
                    set({
                      [`aciklama_${form.dil}`]:
                        event.target.value,
                    })
                  }
                  sx={fieldSx}
                />
              </Stack>
            </Box>
          </SectionCard>

          <SectionCard
            icon={<ImageIcon />}
            title={TT.visualSection}
            description={
              TT.visualSectionDesc
            }
            accent={COLORS.orange}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0,1fr)",
                  md: "minmax(260px,4fr) minmax(0,8fr)",
                },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  minHeight: {
                    xs: 190,
                    md: 230,
                  },
                  overflow: "hidden",
                  borderRadius: 3,
                  border: `1px solid ${COLORS.border}`,
                  bgcolor: "#f1f5f9",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {form.resim ? (
                  <Box
                    component="img"
                    src={form.resim}
                    alt="preview"
                    sx={{
                      width: "100%",
                      height: "100%",
                      minHeight: {
                        xs: 190,
                        md: 230,
                      },
                      display: "block",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Stack
                    alignItems="center"
                    spacing={0.75}
                    sx={{
                      color: "#94a3b8",
                    }}
                  >
                    <ImageIcon
                      sx={{ fontSize: 44 }}
                    />

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 750 }}
                    >
                      {TT.noImage}
                    </Typography>
                  </Stack>
                )}
              </Box>

              <Stack
                spacing={1.5}
                justifyContent="center"
                minWidth={0}
              >
                <TextField
                  fullWidth
                  label={TT.imageUrl}
                  value={form.resim}
                  onChange={(event) =>
                    set({
                      resim:
                        event.target.value,
                    })
                  }
                  sx={fieldSx}
                />

                <ResimUpload
                  label={TT.uploadImage}
                  defaultPreview={
                    form.resim
                  }
                  onUpload={(url) =>
                    set({ resim: url })
                  }
                />

                <Typography
                  variant="caption"
                  sx={{
                    color: COLORS.muted,
                    fontWeight: 700,
                  }}
                >
                  {TT.imageHelp}
                </Typography>
              </Stack>
            </Box>

            <TextField
              fullWidth
              label={TT.linkLbl}
              value={form.link}
              onChange={(event) =>
                set({
                  link: event.target.value,
                })
              }
              sx={{
                ...fieldSx,
                mt: 1.75,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InsertLinkIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          </SectionCard>

          <SectionCard
            icon={<CalendarMonthIcon />}
            title={TT.publishSection}
            description={
              TT.publishSectionDesc
            }
            accent={COLORS.green}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0,1fr)",
                  md: "repeat(2,minmax(0,1fr))",
                },
                gap: 1.5,
                alignItems: "stretch",
              }}
            >
              <TextField
                fullWidth
                type="date"
                label={TT.date}
                value={form.tarih}
                onChange={(event) =>
                  set({
                    tarih:
                      event.target.value,
                  })
                }
                InputLabelProps={{
                  shrink: true,
                }}
                sx={fieldSx}
              />

              <Paper
                variant="outlined"
                sx={{
                  minHeight: 56,
                  px: 1.5,
                  py: 0.75,
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "space-between",
                  gap: 1,
                  borderRadius: 2.5,
                  borderColor: form.aktif
                    ? "#a7f3d0"
                    : COLORS.border,
                  bgcolor: form.aktif
                    ? COLORS.greenSoft
                    : "#f8fafc",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <CheckCircleIcon
                    sx={{
                      color: form.aktif
                        ? COLORS.green
                        : "#94a3b8",
                    }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        color: COLORS.text,
                        fontWeight: 850,
                      }}
                    >
                      {TT.status}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: form.aktif
                          ? COLORS.green
                          : COLORS.muted,
                        fontWeight: 750,
                      }}
                    >
                      {form.aktif
                        ? TT.active
                        : TT.passive}
                    </Typography>
                  </Box>
                </Stack>

                <Switch
                  checked={form.aktif}
                  onChange={(event) =>
                    set({
                      aktif:
                        event.target.checked,
                    })
                  }
                />
              </Paper>
            </Box>
          </SectionCard>

          <Paper
            sx={{
              position: "sticky",
              bottom: 8,
              zIndex: 10,
              p: {
                xs: 1.15,
                sm: 1.5,
              },
              borderRadius: 3,
              border: `1px solid ${COLORS.border}`,
              bgcolor:
                "rgba(255,255,255,.94)",
              backdropFilter: "blur(14px)",
              boxShadow:
                "0 16px 36px rgba(15,23,42,.13)",
            }}
          >
            <Stack
              direction={{
                xs: "column-reverse",
                sm: "row",
              }}
              spacing={1}
              justifyContent="flex-end"
            >
              <Button
                type="button"
                startIcon={
                  <ArrowBackIcon />
                }
                onClick={() =>
                  redirect("/duyurular")
                }
                sx={{
                  minHeight: 48,
                  px: 2.5,
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontWeight: 850,
                  color: "#334155",
                  bgcolor: "#f1f5f9",
                }}
              >
                {TT.cancel}
              </Button>

              <Button
                type="submit"
                disabled={saving}
                startIcon={
                  saving ? (
                    <CircularProgress
                      size={18}
                      color="inherit"
                    />
                  ) : (
                    <SaveIcon />
                  )
                }
                sx={{
                  minHeight: 48,
                  px: 3,
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontWeight: 900,
                  color: "#fff",
                  bgcolor: COLORS.blue,
                  boxShadow:
                    "0 10px 24px rgba(37,99,235,.22)",

                  "&:hover": {
                    bgcolor: "#1d4ed8",
                  },
                }}
              >
                {saving
                  ? TT.saving
                  : TT.save}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}

export default DuyurularPage;